// React / Next
import { useEffect, useRef, useState, useCallback } from 'react';

// Styles
import styles from './DiagonalSpinVector.module.css';

// Types
interface DiagonalSpinVectorProps {
  className?: string;
  width?: number;
  height?: number;
}

// Animation duration in ms (base duration + max delay for all paths)
const ANIMATION_DURATION = 700 + 35 * 15; // 0.7s base + ~35 paths * 0.015s delay

/**
 * DiagonalSpinVector - Animated SVG with diagonal spin wave effect on hover
 * Paths animate with scale and rotation from top-left to bottom-right diagonal
 */
export const DiagonalSpinVector = ({
  className = '',
  width = 496,
  height = 455,
}: DiagonalSpinVectorProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const paths = Array.from(svg.querySelectorAll('g path')) as SVGPathElement[];

    // Calculate diagonal index based on X + Y position
    const items = paths.map((path) => {
      const box = path.getBBox();
      // Divide by factor to group nearby elements
      const diagVal = (box.x + box.y) / 10;
      return { element: path, val: diagVal };
    });

    // Sort by diagonal value (top-left to bottom-right)
    items.sort((a, b) => a.val - b.val);

    // Assign diagonal index
    items.forEach((item, index) => {
      item.element.style.setProperty('--diag-i', String(index));
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  }, [isAnimating]);

  return (
    <svg
      ref={svgRef}
      className={`${styles.svg} ${isAnimating ? styles.animating : ''} ${className}`}
      width={width}
      height={height}
      viewBox="0 0 496 455"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Diagonal spin animated vector"
      role="img"
      onMouseEnter={handleMouseEnter}
    >
      <rect width="496" height="455" fill="#FF6F00" />
      <g clipPath="url(#diagonalSpinClip)">
        <path d="M103.882 287.32H102.907L100 54H106.789L103.882 287.32Z" fill="#042E3F" />
        <path d="M121.297 54L118.491 287.32H117.532L114.799 54H121.297Z" fill="#042E3F" />
        <path d="M135.806 54L133.117 287.32H132.157L129.613 54H135.806Z" fill="#042E3F" />
        <path d="M150.314 54L147.741 287.32H146.782L144.412 54H150.314Z" fill="#042E3F" />
        <path d="M164.823 54L162.366 287.32H161.406L159.226 54H164.823Z" fill="#042E3F" />
        <path d="M179.331 54L176.99 287.32H176.016L174.024 54H179.331Z" fill="#042E3F" />
        <path d="M193.84 54L191.615 287.32H190.641L188.824 54H193.84Z" fill="#042E3F" />
        <path d="M208.362 54L206.24 287.32H205.266L203.638 54H208.362Z" fill="#042E3F" />
        <path d="M222.87 54L220.864 287.32H219.89L218.437 54H222.87Z" fill="#042E3F" />
        <path d="M237.38 54L235.475 287.32H234.516L233.236 54H237.38Z" fill="#042E3F" />
        <path d="M251.888 54L250.1 287.32H249.14L248.05 54H251.888Z" fill="#042E3F" />
        <path d="M266.396 54L264.724 287.32H263.765L262.849 54H266.396Z" fill="#042E3F" />
        <path d="M280.905 54L279.349 287.32H278.39L277.663 54H280.905Z" fill="#042E3F" />
        <path d="M295.428 54L293.974 287.32H293L292.462 54H295.428Z" fill="#042E3F" />
        <path d="M309.936 54L308.598 287.32H307.624L307.261 54H309.936Z" fill="#042E3F" />
        <path d="M324.444 54L323.223 287.32H322.249L322.074 54H324.444Z" fill="#042E3F" />
        <path d="M337.848 287.32H336.874V54H338.953L337.848 287.32Z" fill="#042E3F" />
        <path d="M388.118 167.68H389.092L392 401H385.211L388.118 167.68Z" fill="#15B1CD" />
        <path d="M370.702 401L373.508 167.68H374.467L377.2 401H370.702Z" fill="#15B1CD" />
        <path d="M356.193 401L358.883 167.68H359.842L362.386 401H356.193Z" fill="#15B1CD" />
        <path d="M341.686 401L344.259 167.68H345.218L347.588 401H341.686Z" fill="#15B1CD" />
        <path d="M327.177 401L329.634 167.68H330.593L332.774 401H327.177Z" fill="#15B1CD" />
        <path d="M312.669 401L315.009 167.68H315.984L317.975 401H312.669Z" fill="#15B1CD" />
        <path d="M298.146 401L300.384 167.68H301.358L303.175 401H298.146Z" fill="#15B1CD" />
        <path d="M283.637 401L285.759 167.68H286.733L288.361 401H283.637Z" fill="#15B1CD" />
        <path d="M269.129 401L271.135 167.68H272.109L273.563 401H269.129Z" fill="#15B1CD" />
        <path d="M254.62 401L256.525 167.68H257.484L258.763 401H254.62Z" fill="#15B1CD" />
        <path d="M240.112 401L241.9 167.68H242.86L243.95 401H240.112Z" fill="#15B1CD" />
        <path d="M225.604 401L227.275 167.68H228.235L229.151 401H225.604Z" fill="#15B1CD" />
        <path d="M211.095 401L212.65 167.68H213.61L214.337 401H211.095Z" fill="#15B1CD" />
        <path d="M196.572 401L198.026 167.68H199L199.538 401H196.572Z" fill="#15B1CD" />
        <path d="M182.063 401L183.401 167.68H184.375L184.738 401H182.063Z" fill="#15B1CD" />
        <path d="M167.556 401L168.777 167.68H169.751L169.925 401H167.556Z" fill="#15B1CD" />
        <path d="M154.152 167.68H155.126V401H153.047L154.152 167.68Z" fill="#15B1CD" />
      </g>
      <defs>
        <clipPath id="diagonalSpinClip">
          <rect width="292" height="347" fill="white" transform="translate(100 54)" />
        </clipPath>
      </defs>
    </svg>
  );
};

